/* NoValueStates.ts */

export const NOT_LOOKED_FOR_LABEL = "Not looked for";
export const LOOKED_FOR_NO_VALUE_LABEL = "Looked for, no value";

// Label for the has-value slice in the single-group completeness pie. Sits
// alongside the two no-value states, so it names that state ("With value")
// rather than the group ("Full sample"), which would be misleading - the full
// sample also contains the not-looked-for / looked-for-no-value patients.
export const HAS_VALUE_LABEL = "With value";

/**
 * Whether a group/category name is one of the two no-value states
 * ("Not looked for" / "Looked for, no value").
 */
export const isNoValueStateLabel = (
  name?: string | null
): boolean =>
  name === NOT_LOOKED_FOR_LABEL ||
  name === LOOKED_FOR_NO_VALUE_LABEL;

/**
 * Order cohort groups by information-content quality: real values first (in
 * their incoming order - the backend already sorts them alphabetically /
 * numerically / by custom order, which must be preserved), then "Looked for,
 * no value" (a value was sought but none found), then "Not looked for" (never
 * assessed) last. Applied to the legend/pie AND every chart so a group's
 * position - and therefore its position-based colour - stays consistent
 * everywhere it appears.
 */
export const orderCohortGroups = <
  T extends { name?: string | null }
>(
  data: T[]
): T[] => [
  ...data.filter((entry) => !isNoValueStateLabel(entry.name)),
  ...data.filter(
    (entry) => entry.name === LOOKED_FOR_NO_VALUE_LABEL
  ),
  ...data.filter(
    (entry) => entry.name === NOT_LOOKED_FOR_LABEL
  ),
];

/**
 * Move the two no-value states to the end of an x-axis category list, in the
 * same information-quality order as `orderCohortGroups`: real categories
 * first (unchanged), then "Looked for, no value", then "Not looked for".
 */
export const orderNoValueCategoriesLast = (
  categories: string[]
): string[] => [
  ...categories.filter(
    (category) => !isNoValueStateLabel(category)
  ),
  ...categories.filter(
    (category) => category === LOOKED_FOR_NO_VALUE_LABEL
  ),
  ...categories.filter(
    (category) => category === NOT_LOOKED_FOR_LABEL
  ),
];

/**
 * When a feature is selected as the cohort-grouping dimension, patients
 * whose cohort-defining feature itself has no value are surfaced by the
 * backend as their own cohort groups, named exactly `NOT_LOOKED_FOR_LABEL`/
 * `LOOKED_FOR_NO_VALUE_LABEL` - the same response every request always
 * includes them in (the "show no-value entries" toggle only controls
 * client-side display, not what's fetched). Exclude them when the toggle
 * is off, so they only appear once the user opts in.
 *
 * Also exclude any *real* cohort group whose `total` (has-value count for
 * the analyzed feature) is 0: such a group only exists in the response at
 * all because every one of its members lacks a real value (they only have
 * "not looked for"/"looked for, no value" states) - the backend always
 * includes it so it can contribute to the no-value categories once the
 * toggle is on. With the toggle off there is nothing real to plot for it,
 * so it must not render as an empty 0-member bar/legend entry.
 */
export const excludeCohortNoValueGroups = <
  T extends { name?: string | null; total?: number | null }
>(
  data: T[],
  showNoValueEntries: boolean
): T[] =>
  showNoValueEntries
    ? data
    : data.filter(
        (entry) =>
          entry.name !== NOT_LOOKED_FOR_LABEL &&
          entry.name !== LOOKED_FOR_NO_VALUE_LABEL &&
          entry.total !== 0
      );

/**
 * The patient count to display for a cohort-grouped chart. With the toggle
 * off we only show the real cohort groups, so the count must be the sum of
 * their has-value totals - i.e. patients with BOTH a real feature value AND a
 * real cohort value ("complete cases"). This drops patients who have a real
 * feature value but no cohort value, whose group is hidden, so the number
 * matches the visible bars instead of over-counting them.
 */
export const sumVisibleGroupTotals = <
  T extends { name?: string | null; total?: number | null }
>(
  data: T[]
): number =>
  excludeCohortNoValueGroups(data, false).reduce(
    (sum, group) => sum + (group.total ?? 0),
    0
  );

/**
 * The cohort groups whose cohort-defining value was *looked for* - i.e. every
 * group except the "Not looked for" cohort pseudo-group. Patients in the "Not
 * looked for" group have no cohort value at all (it was never assessed), so
 * they fail the cohort side of the "feature looked-for AND cohort looked-for"
 * rule and must be excluded from the numerator. The "Looked for, no value"
 * cohort group IS kept (its cohort value was sought). Real cohort groups with
 * a 0 has-value total are also kept, because their looked-for-no-value members
 * still satisfy the rule.
 */
export const cohortLookedForGroups = <
  T extends {
    name?: string | null;
    total?: number | null;
    lookedForNoValueCount?: number | null;
  }
>(
  data: T[]
): T[] => data.filter((entry) => entry.name !== NOT_LOOKED_FOR_LABEL);

/**
 * Sum of the has-value `total` across the given cohort groups.
 */
export const sumGroupTotals = <
  T extends { total?: number | null }
>(
  data: T[]
): number =>
  data.reduce((sum, group) => sum + (group.total ?? 0), 0);

/**
 * The looked-for-no-value patient count summed across the given cohort groups.
 * The displayed numerator `n` (patients) is "looked for" = has-value +
 * looked-for-no-value once the toggle is on. With a cohort selected those
 * looked-for-no-value counts live per group as `lookedForNoValueCount`; this
 * aggregates them so the numerator becomes `total + looked-for-no-value`,
 * matching the no-cohort case (where the single group's count equals the
 * response-level total). Callers pass `cohortLookedForGroups(...)` so the "Not
 * looked for" cohort group is already excluded. Returns `undefined` when no
 * group carries the field (e.g. non-descriptive charts), so callers can fall
 * back cleanly.
 */
export const sumGroupLookedForNoValue = <
  T extends { lookedForNoValueCount?: number | null }
>(
  data: T[]
): number | undefined =>
  data.some((group) => group.lookedForNoValueCount != null)
    ? data.reduce(
        (sum, group) => sum + (group.lookedForNoValueCount ?? 0),
        0
      )
    : undefined;
